import {
  bulkUpdateNotification,
  getAllNotificationService,
} from "@/service/notification.service";
import { refetchCourseCount, selectUser } from "@/store/slices/authSlice";
import {
  refetchLessonUnitCount,
  setNavigationSource,
} from "@/store/slices/courseSlice";
import { Notification } from "@/types/notification.model";
import { getInitials } from "@/utils/constants";
import { AppDispatch } from "@store";
import { Bell, BellIcon, MailIcon } from "lucide-react";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

function NotificationModule() {
  const notificationRef = useRef<HTMLDivElement>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const initialFetchRef = useRef(false);
  const user = useSelector(selectUser);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // WebSocket reference
  const socketRef = useRef<WebSocket | null>(null);
  const connectionAttemptedRef = useRef(false);

  // Reusable function to fetch notifications
  const fetchNotifications = useCallback(
    async (pageNumber: number, append: boolean = false) => {
      if (loading) return;

      setLoading(true);
      try {
        const { data } = await getAllNotificationService({
          page: pageNumber,
          pageSize: 10, // Number of items to load per page
        });

        // Extract pagination information
        const pageInfo = data.pageInfo || {};
        const totalPages = (pageInfo as any).total_pages || 1;
        const currentPage = (pageInfo as any).current_page || pageNumber;
        const newNotifications = data.data.notifications || [];

        console.log("Notification API response:", {
          pageInfo,
          totalPages,
          currentPage,
          notificationsCount: newNotifications.length,
        });

        // Update hasMore based on current page and total pages
        const hasMorePages = currentPage < totalPages;
        setHasMore(hasMorePages);
        console.log(
          "Has more pages:",
          hasMorePages,
          "Current:",
          currentPage,
          "Total:",
          totalPages
        );

        // Update unread count if this is an initial fetch (not append)
        if (!append) {
          setUnreadCount(data.data.unread_count || 0);
        }

        // Handle notifications based on whether we're appending or replacing
        if (append) {
          setNotifications((prev) => [...prev, ...newNotifications]);
        } else {
          // For initial load or refresh, just set the notifications directly
          setNotifications(newNotifications);
        }

        return data;
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
        setHasMore(false); // Ensure hasMore is false on error
        return null;
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  // Load more notifications when scrolling
  const loadMoreNotifications = useCallback(() => {
    if (!hasMore) {
      console.log("No more notifications to load, hasMore is false");
      return;
    }

    console.log("Loading more notifications, current page:", page);
    const nextPage = page + 1;

    fetchNotifications(nextPage, true).then((data) => {
      if (data) {
        console.log("Successfully loaded page:", nextPage);
        setPage(nextPage);
      } else {
        console.log("Failed to load more notifications");
        setHasMore(false);
      }
    });
  }, [fetchNotifications, hasMore, page]);

  // Initial fetch of notifications when component mounts
  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchNotifications(1, false).then(() => {
        initialFetchRef.current = true;
      });
    }
  }, [fetchNotifications]);

  // Close notification popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationRef]);

  // Initialize WebSocket connection
  const connectWebSocket = () => {
    // Only connect if we have a user ID and no active connection
    if (!user || socketRef.current || connectionAttemptedRef.current) {
      return;
    }

    const { user_id } = user;
    console.log("Connecting to WebSocket with user ID:", user_id);
    connectionAttemptedRef.current = true;

    const socket = new WebSocket(
      `wss://musictemple-api.underdev.in/ws/notifications/?user_id=${user_id}`
    );
    socketRef.current = socket;

    socket.onmessage = (event) => {
      console.log("Raw WebSocket message received:", event.data);
      try {
        const data = JSON.parse(event.data);
        console.log("Parsed WebSocket message:", data);

        // Handle new notification
        if (data && data.message) {
          const newNotification =
            typeof data.message === "string"
              ? JSON.parse(data.message)
              : data.message;

          dispatch(refetchCourseCount());
          if (newNotification.redirect_obj?.grade) {
            dispatch(
              refetchLessonUnitCount({
                gradeId: newNotification.redirect_obj.grade,
                studentId: null,
              })
            );
          }

          // Check if notification already exists in the list
          setNotifications((prev) => {
            // Check if this notification ID already exists in our list
            const exists = prev.some(
              (notification) => notification.id === newNotification.id
            );
            if (exists) {
              // If it exists, don't add it again
              return prev;
            } else {
              // If it's new, add it to the top of the list
              return [newNotification, ...prev];
            }
          });

          // Increment unread count only for new notifications
          setUnreadCount((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onclose = () => {
      console.log("WebSocket closed.");
      socketRef.current = null;
      connectionAttemptedRef.current = false;
    };

    socket.onerror = (e) => {
      console.error("WebSocket error:", e);
      socketRef.current = null;
      connectionAttemptedRef.current = false;
    };
  };

  useEffect(() => {
    if (user?.user_id) {
      connectWebSocket();
    }

    return () => {
      if (socketRef.current) {
        console.log("Closing WebSocket connection");
        socketRef.current.close();
        socketRef.current = null;
      }
      connectionAttemptedRef.current = false;
    };
  }, [user?.user_id]);

  // Handle marking all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      const response = await bulkUpdateNotification({ isRead: true });
      // Proceed if the request was successful
      if (response && response.status === 200) {
        // Reset pagination
        setPage(1);

        // Refetch notifications using the common function
        await fetchNotifications(1, false);
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  // Handle clearing all notifications
  const handleClearAll = async () => {
    try {
      const response = await bulkUpdateNotification({ isClear: true });
      // Proceed if the request was successful
      if (response && response.status === 200) {
        // Reset pagination and clear notifications
        setPage(1);
        setHasMore(false); // Set to false since we're clearing all notifications
        setUnreadCount(0);
        setNotifications([]);
        setNotificationOpen(false);
        // No need to refetch since we've cleared all notifications
        console.log("All notifications cleared successfully");
      }
    } catch (error) {
      console.error("Failed to clear all notifications:", error);
    }
  };

  const handleNavigation = async (notification: Notification) => {
    try {
      const response = await bulkUpdateNotification({
        isRead: true,
        notificationId: notification.id,
      });
      // Proceed if the request was successful
      if (response && response.status === 200) {
        // Reset pagination
        setPage(1);

        // Clear existing notifications before fetching new ones
        setNotifications([]);

        // Refetch notifications using the common function
        await fetchNotifications(1, false);
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }

    const gradeId = notification.redirect_obj?.grade;

    if (gradeId) {
      const type = notification.redirect_obj?.type;

      dispatch(setNavigationSource("notification"));

      navigate(`/course-management/view-course/${gradeId}`, {
        state: {
          selectedTab: type === "ASSIGNMENT" ? "assignment" : "feedback",
          selectedStudentId: notification.redirect_obj?.student,
          selectedCardId:
            type === "ASSIGNMENT"
              ? notification.redirect_obj?.assignment
              : notification.redirect_obj?.query,
          assignmentType:
            notification.redirect_obj?.type === "ASSIGNMENT" &&
            notification.redirect_obj?.lesson
              ? "unit"
              : "general",
          lessonId: notification.redirect_obj?.lesson || null,
          unitId: notification.redirect_obj?.unit || null,
        },
      });

      setNotificationOpen(false);
    }
  };

  return (
    <div className="relative" ref={notificationRef}>
      <button
        className="text-gray-600 w-[56px] h-[56px] focus:outline-none bg-[#F5FBFF] rounded-full p-2 flex items-center justify-center cursor-pointer"
        onClick={() => setNotificationOpen(!notificationOpen)}
      >
        <div className="relative">
          <Bell className="w-6 h-6 text-[#1F1F1F]" />
          {unreadCount > 0 && (
            <span className="absolute top-[-2px] right-[-2px] w-2 h-2 bg-[#C53921] rounded-full"></span>
          )}
        </div>
      </button>

      {notificationOpen && (
        <div className="absolute right-0 mt-2 w-[486px] bg-white rounded-lg shadow-lg z-50 border border-gray-100 overflow-hidden">
          <div
            className="flex justify-between items-center px-6 py-4 border-b border-gray-100"
            style={{
              boxShadow: "0px 4px 12px 0px #00000014",
            }}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-[16px] text-[#141414] font-semibold">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="bg-[#5C88C5] text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="text-[#5C88C5] text-xs font-medium hover:disabled:no-underline hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-default"
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0 || notifications.length === 0}
              >
                Mark all as read
              </button>
              <button
                className="text-[#ef4444] text-xs font-medium hover:disabled:no-underline hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-default"
                onClick={handleClearAll}
                disabled={notifications.length === 0}
              >
                Clear all
              </button>
            </div>
          </div>

          <div
            id="notificationScrollContainer"
            className="max-h-[318px] min-h-[120px] overflow-y-auto"
          >
            {notifications.length > 0 ? (
              <InfiniteScroll
                height={318}
                scrollThreshold={0.5} // Lower threshold to trigger earlier
                dataLength={notifications.length}
                next={loadMoreNotifications}
                hasMore={hasMore}
                loader={
                  <div className="flex justify-center items-center py-2">
                    <div className="w-5 h-5 border-2 border-t-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="ml-2 text-sm text-gray-500">
                      Loading...
                    </span>
                  </div>
                }
                // endMessage={
                //   <div className="text-center py-2">
                //     <span className="text-sm text-gray-400">
                //       No more notifications
                //     </span>
                //   </div>
                // }
                scrollableTarget="notificationScrollContainer"
              >
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-6 py-4 border-b border-gray-100 cursor-pointer ${
                      !notification.is_viewed
                        ? "bg-[#FFF5F9]"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => handleNavigation(notification)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-[40px] h-[40px] bg-gray-200 text-[#1F1F1F] rounded-full flex items-center justify-center">
                        {getInitials(notification?.creator?.name || "-")}
                      </div>
                      <div className="flex-1">
                        {notification?.redirect_obj?.title && (
                          <div className="flex items-center gap-2">
                            {notification?.source === "FEEDBACK" ||
                            notification?.source === "QUERY_FEEDBACK" ? (
                              <MailIcon className="w-[18px] h-[18px]" />
                            ) : (
                              <BellIcon className="w-[18px] h-[18px]" />
                            )}

                            <h4 className="font-semibold text-[#141414] mb-1">
                              {notification.redirect_obj?.title || "Assignment"}
                            </h4>
                          </div>
                        )}
                        <p className="text-[#696F8C] text-[14px] font-normal mb-2">
                          {notification.content}
                        </p>
                        <p className="text-[#696F8C] text-[14px] font-normal">
                          {moment(notification.created_on).fromNow()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </InfiniteScroll>
            ) : (
              <div className="flex items-center justify-center min-h-[120px]">
                <div className="p-4 text-center text-gray-500">
                  {loading ? "Loading notifications..." : "No notifications"}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationModule;
